import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, ApiError } from '../../lib/api';
import { AuthLayout, AuthField, AuthSubmit, AuthError } from './AuthLayout';

export default function SetPassword(): ReactElement {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (password !== confirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await authApi.resetPassword(token, password, code.trim() || undefined);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Passwort konnte nicht gesetzt werden.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title="Link ungültig" subtitle="Dieser Link ist unvollständig oder abgelaufen.">
        <p className="text-gray-700">Bitte fordere einen neuen Link an.</p>
        <p className="mt-6 text-center text-sm text-gray-600">
          <Link to="/passwort-vergessen" className="font-semibold text-blue-600 hover:text-blue-700">
            Neuen Link anfordern
          </Link>
        </p>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Passwort gesetzt ✓" subtitle="Du wirst zur Anmeldung weitergeleitet …">
        <p className="text-gray-700">Du kannst dich jetzt mit deinem neuen Passwort anmelden.</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Neues Passwort vergeben" subtitle="Wähle ein sicheres Passwort für dein Konto.">
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {error && <AuthError message={error} />}
        <AuthField
          label="Persönlicher Aktivierungs-Code"
          type="text"
          value={code}
          onChange={setCode}
          placeholder="z. B. AB23-CD45"
          hint="Bestandskunden: Bitte gib den Code ein, den du von uns erhalten hast. Neue Konten lassen das Feld leer."
        />
        <AuthField
          label="Neues Passwort"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          placeholder="Mindestens 8 Zeichen"
          required
        />
        <AuthField
          label="Passwort bestätigen"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
          placeholder="Passwort wiederholen"
          required
        />
        <AuthSubmit busy={busy}>Passwort speichern</AuthSubmit>
      </form>
    </AuthLayout>
  );
}
